import { useEffect, useRef, useState } from 'react';
import { useTourContext } from '@/store/tourContext';
import { tutorialActions } from '@/utils/tutorials';
import { useTutorialActive } from './useTutorialActive';

interface UseBVTutorialProps {
	benefitVerificationTableRef: React.RefObject<HTMLDivElement | null>;
	onCloseDemoBVModal?: () => void;
}

import type { ColumnData } from '@/components/Organisms/DataTable/DataTable.types';

interface UseBVTutorialReturn {
	shouldOpenFirstRowMenu: boolean;
	shouldOpenSecondRowMenu: boolean;
	isDemoIVRModalOpen: boolean;
	isDemoOrderNowModalOpen: boolean;
	setIsDemoIVRModalOpen: (open: boolean) => void;
	setIsDemoOrderNowModalOpen: (open: boolean) => void;
	mockedActions: {
		handleViewIVR: (item: ColumnData) => void;
		handleVoidNow: (item: ColumnData) => void;
		handleOrderNow: (item: ColumnData) => void;
		handleReorder: (item: ColumnData) => void;
		handleEdit: (item: ColumnData) => void;
		handleDetails: (item: ColumnData) => void;
		handleViewPRDocument: (item: ColumnData) => void;
		handleViewDocument: (item: ColumnData) => void;
		handleSupportTicket: (item: ColumnData) => void;
	};
}

/**
 * Custom hook to manage all Benefit Verification tutorial logic
 * Handles tutorial actions, menu states, and ref registrations
 */
export const useBVTutorial = ({
	benefitVerificationTableRef,
	onCloseDemoBVModal,
}: UseBVTutorialProps): UseBVTutorialReturn => {
	const { registerElement } = useTourContext();
	const { isBVTutorialActive: isDemoBVTutorialActive } = useTutorialActive();

	const [shouldOpenFirstRowMenu, setShouldOpenFirstRowMenu] = useState(false);
	const [shouldOpenSecondRowMenu, setShouldOpenSecondRowMenu] = useState(false);
	const [isDemoIVRModalOpen, setIsDemoIVRModalOpen] = useState(false);
	const [isDemoOrderNowModalOpen, setIsDemoOrderNowModalOpen] = useState(false);
	
	// Track current menu state using refs (for accurate reading in actions). Only 2 rows: first (In Process), second (Approved with IVR/Order Now).
	const currentMenuStateRef = useRef<{
		firstRow: boolean;
		secondRow: boolean;
	}>({
		firstRow: false,
		secondRow: false,
	});
	
	// Track which menu was open before closing (for restoring when going back)
	const previousMenuStateRef = useRef<{
		firstRow: boolean;
		secondRow: boolean;
	}>({
		firstRow: false,
		secondRow: false,
	});
	
	const isTutorialActiveRef = useRef(isDemoBVTutorialActive);
	
	const pendingTimeoutsRef = useRef<NodeJS.Timeout[]>([]);
	
	useEffect(() => {
		currentMenuStateRef.current = {
			firstRow: shouldOpenFirstRowMenu,
			secondRow: shouldOpenSecondRowMenu,
		};
	}, [shouldOpenFirstRowMenu, shouldOpenSecondRowMenu]);
	
	useEffect(() => {
		isTutorialActiveRef.current = isDemoBVTutorialActive;
	}, [isDemoBVTutorialActive]);

	// Register tutorial actions
	useEffect(() => {
		tutorialActions.register('closeBVCreateModal', () => {
			if (!isDemoBVTutorialActive) return;
			onCloseDemoBVModal?.();
			// After closing the modal, open the hamburger menu of the first row
			const timeoutId = setTimeout(() => {
				if (!isTutorialActiveRef.current) return;
				setShouldOpenFirstRowMenu(true);
				// Simulate click on the hamburger button of the first row
				if (benefitVerificationTableRef.current) {
					const tbody =
						benefitVerificationTableRef.current.querySelector('tbody');
					if (tbody) {
						const firstRow = tbody.querySelector('tr:first-child');
						if (firstRow) {
							const cells = firstRow.querySelectorAll('td');
							if (cells.length > 0) {
								const actionCell = cells[cells.length - 1];
								const trigger = actionCell.querySelector(
									'button, [role="button"]',
								) as HTMLElement;
								if (trigger) {
									trigger.click();
								}
							}
						}
					}
				}
			}, 300);
			// Store timeout ID to cancel it if tutorial ends
			pendingTimeoutsRef.current.push(timeoutId);
		});

		tutorialActions.register('clickActionHamburgerRef', () => {
			if (!isDemoBVTutorialActive) return;
			// Save current menu state before closing (to restore when going back)
			previousMenuStateRef.current = {
				firstRow: currentMenuStateRef.current.firstRow,
				secondRow: currentMenuStateRef.current.secondRow,
			};
			
			// Close other menus first (explicitly close in DOM if open)
			if (benefitVerificationTableRef.current) {
				const tbody = benefitVerificationTableRef.current.querySelector('tbody');
				if (tbody) {
					// Close second row menu if open
					if (currentMenuStateRef.current.secondRow) {
						setShouldOpenSecondRowMenu(false);
						const rows = tbody.querySelectorAll('tr');
						if (rows.length > 1) {
							const secondRow = rows[1];
							const cells = secondRow.querySelectorAll('td');
							if (cells.length > 0) {
								const actionCell = cells[cells.length - 1];
								const trigger = actionCell.querySelector(
									'button, [role="button"]',
								) as HTMLElement;
								if (trigger) {
									trigger.click();
								}
							}
						}
					}
					// Close third row menu if open
					if (currentMenuStateRef.current.secondRow) {
						setShouldOpenSecondRowMenu(false);
						const rows = tbody.querySelectorAll('tr');
						if (rows.length > 1) {
							const secondRow = rows[1];
							const cells = secondRow.querySelectorAll('td');
							if (cells.length > 0) {
								const actionCell = cells[cells.length - 1];
								const trigger = actionCell.querySelector(
									'button, [role="button"]',
								) as HTMLElement;
								if (trigger) {
									trigger.click();
								}
							}
						}
					}
				}
			}
			
			setShouldOpenSecondRowMenu(false);
			setShouldOpenFirstRowMenu(true);
		});

		tutorialActions.register('closeBVActions', () => {
			if (!isDemoBVTutorialActive) return;
			// Save current menu state before closing (to restore when going back)
			previousMenuStateRef.current = {
				firstRow: currentMenuStateRef.current.firstRow,
				secondRow: currentMenuStateRef.current.secondRow,
			};
			// Close all menus (used when moving forward from step 11)
			// Don't open any menu here - the next step will handle opening its menu
			setShouldOpenFirstRowMenu(false);
			setShouldOpenSecondRowMenu(false);
			
			// Explicitly close the first row menu in the DOM if it's open
			if (currentMenuStateRef.current.firstRow && benefitVerificationTableRef.current) {
				const tbody = benefitVerificationTableRef.current.querySelector('tbody');
				if (tbody) {
					const firstRow = tbody.querySelector('tr:first-child');
					if (firstRow) {
						const cells = firstRow.querySelectorAll('td');
						if (cells.length > 0) {
							const actionCell = cells[cells.length - 1];
							const trigger = actionCell.querySelector(
								'button, [role="button"]',
							) as HTMLElement;
							if (trigger) {
								// Click to close the menu
								trigger.click();
							}
						}
					}
				}
			}
		});

		// Action to open first row menu (used in onPrevAction for steps 11 and 12)
		// This opens the first row menu so that the Edit option can be highlighted
		tutorialActions.register('openFirstRowMenuAction', () => {
			if (!isDemoBVTutorialActive) return;
			// Close other menus first
			setShouldOpenSecondRowMenu(false);
			setTimeout(() => {
				if (!isTutorialActiveRef.current) return;
				setShouldOpenFirstRowMenu(true);
				// Simulate click on the hamburger button of the first row
				if (benefitVerificationTableRef.current) {
					const tbody =
						benefitVerificationTableRef.current.querySelector('tbody');
					if (tbody) {
						const firstRow = tbody.querySelector('tr:first-child');
						if (firstRow) {
							const cells = firstRow.querySelectorAll('td');
							if (cells.length > 0) {
								const actionCell = cells[cells.length - 1];
								const trigger = actionCell.querySelector(
									'button, [role="button"]',
								) as HTMLElement;
								if (trigger) {
									trigger.click();
								}
							}
						}
					}
				}
			}, 100);
		});
		
		// Action to close first row menu when highlighting the second row hamburger
		// This is executed when the second row hamburger is highlighted (step 12)
		tutorialActions.register('closeFirstRowMenuForSecondRow', () => {
			if (!isDemoBVTutorialActive) return;
			// Close the first row menu when highlighting the second row hamburger
			setShouldOpenFirstRowMenu(false);
			// Explicitly close the first row menu in the DOM
			if (currentMenuStateRef.current.firstRow && benefitVerificationTableRef.current) {
				const tbody = benefitVerificationTableRef.current.querySelector('tbody');
				if (tbody) {
					const firstRow = tbody.querySelector('tr:first-child');
					if (firstRow) {
						const cells = firstRow.querySelectorAll('td');
						if (cells.length > 0) {
							const actionCell = cells[cells.length - 1];
							const trigger = actionCell.querySelector(
								'button, [role="button"]',
							) as HTMLElement;
							if (trigger) {
								// Click to close the menu
								trigger.click();
							}
						}
					}
				}
			}
		});

		tutorialActions.register('clickApprovedActionHamburgerRef', () => {
			if (!isDemoBVTutorialActive) return;
			// Save current menu state before closing (to restore when going back)
			previousMenuStateRef.current = {
				firstRow: currentMenuStateRef.current.firstRow,
				secondRow: currentMenuStateRef.current.secondRow,
			};
			
			// FIRST: Close the previous menu (first row) immediately when highlighting the hamburger
			// This happens when the hamburger icon is highlighted, not when clicking Next
			if (currentMenuStateRef.current.firstRow && benefitVerificationTableRef.current) {
				setShouldOpenFirstRowMenu(false);
				const tbody = benefitVerificationTableRef.current.querySelector('tbody');
				if (tbody) {
					const firstRow = tbody.querySelector('tr:first-child');
					if (firstRow) {
						const cells = firstRow.querySelectorAll('td');
						if (cells.length > 0) {
							const actionCell = cells[cells.length - 1];
							const trigger = actionCell.querySelector(
								'button, [role="button"]',
							) as HTMLElement;
							if (trigger) {
								// Click to close the menu immediately when hamburger is highlighted
								trigger.click();
							}
						}
					}
				}
			}
			
			// Close all menu states
			setShouldOpenFirstRowMenu(false);
			setShouldOpenSecondRowMenu(false);
			
			// THEN: Open the second row menu after a delay
			setTimeout(() => {
				setShouldOpenSecondRowMenu(true);
				// Simulate click on the hamburger button of the second row
				if (benefitVerificationTableRef.current) {
					const tbody =
						benefitVerificationTableRef.current.querySelector('tbody');
					if (tbody) {
						const rows = tbody.querySelectorAll('tr');
						if (rows.length > 1) {
							const secondRow = rows[1];
							const cells = secondRow.querySelectorAll('td');
							if (cells.length > 0) {
								const actionCell = cells[cells.length - 1];
								const trigger = actionCell.querySelector(
									'button, [role="button"]',
								) as HTMLElement;
								if (trigger) {
									trigger.click();
								}
							}
						}
					}
				}
			}, 300);
		});

		tutorialActions.register('openIVRModal', () => {
			if (!isDemoBVTutorialActive) return;
			// Save current menu state before closing (to restore when going back)
			previousMenuStateRef.current = {
				firstRow: currentMenuStateRef.current.firstRow,
				secondRow: currentMenuStateRef.current.secondRow,
			};
			
			setShouldOpenFirstRowMenu(false);
			setShouldOpenSecondRowMenu(false);
			
			if (currentMenuStateRef.current.secondRow && benefitVerificationTableRef.current) {
				const tbody = benefitVerificationTableRef.current.querySelector('tbody');
				if (tbody) {
					const rows = tbody.querySelectorAll('tr');
					if (rows.length > 1) {
						const secondRow = rows[1];
						const cells = secondRow.querySelectorAll('td');
						if (cells.length > 0) {
							const actionCell = cells[cells.length - 1];
							const trigger = actionCell.querySelector(
								'button, [role="button"]',
							) as HTMLElement;
							if (trigger) {
								// Click to close the menu immediately
								trigger.click();
							}
						}
					}
				}
			}
			
			if (currentMenuStateRef.current.secondRow && benefitVerificationTableRef.current) {
				const tbody = benefitVerificationTableRef.current.querySelector('tbody');
				if (tbody) {
					const rows = tbody.querySelectorAll('tr');
					if (rows.length > 1) {
						const secondRow = rows[1];
						const cells = secondRow.querySelectorAll('td');
						if (cells.length > 0) {
							const actionCell = cells[cells.length - 1];
							const trigger = actionCell.querySelector(
								'button, [role="button"]',
							) as HTMLElement;
							if (trigger) {
								trigger.click();
							}
						}
					}
				}
			}
			
			const forceCloseAllMenus = () => {
				const allDivs = document.querySelectorAll('div');
				const openMenus: HTMLElement[] = [];
				
				for (const div of Array.from(allDivs)) {
					const style = window.getComputedStyle(div);
					const zIndex = parseInt(style.zIndex || '0', 10);
					if (
						zIndex >= 1500 &&
						parseFloat(style.borderRadius) > 0 &&
						style.display !== 'none' &&
						style.visibility !== 'hidden' &&
						// Check if it contains menu items from BV table
						(div.textContent?.includes('Edit') ||
							div.textContent?.includes('View IVR') ||
							div.textContent?.includes('Order Now') ||
							div.textContent?.includes('Details') ||
							div.textContent?.includes('Support Ticket') ||
							div.textContent?.includes('Void Now'))
					) {
						openMenus.push(div as HTMLElement);
					}
				}
				
				// If we found open menus, hide them directly
				if (openMenus.length > 0) {
					openMenus.forEach((menu) => {
						(menu as HTMLElement).style.display = 'none';
					});
					
					// Also click outside to close menus
					if (benefitVerificationTableRef.current) {
						benefitVerificationTableRef.current.click();
					}
					const clickEvent = new MouseEvent('click', { bubbles: true, cancelable: true });
					document.body.dispatchEvent(clickEvent);
				}
			};
			
			// Close menus immediately
			forceCloseAllMenus();
			
			// Wait a bit for React to re-render with openOverride=false, then close again
			setTimeout(() => {
				forceCloseAllMenus();
			}, 100);
			
			// Open IVR modal after ensuring all menus are closed
			setTimeout(() => {
				setIsDemoIVRModalOpen(true);
			}, 200);
			
			// When used in onPrevAction, restore the menu that was open before
			setTimeout(() => {
				const previousState = previousMenuStateRef.current;
				if (previousState.secondRow) {
					setShouldOpenSecondRowMenu(true);
					// Simulate click on the hamburger button of the second row
					if (benefitVerificationTableRef.current) {
						const tbody =
							benefitVerificationTableRef.current.querySelector('tbody');
						if (tbody) {
							const rows = tbody.querySelectorAll('tr');
							if (rows.length > 1) {
								const secondRow = rows[1];
								const cells = secondRow.querySelectorAll('td');
								if (cells.length > 0) {
									const actionCell = cells[cells.length - 1];
									const trigger = actionCell.querySelector(
										'button, [role="button"]',
									) as HTMLElement;
									if (trigger) {
										trigger.click();
									}
								}
							}
						}
					}
				}
			}, 250);
		});

		tutorialActions.register('closeIVRModal', () => {
			if (!isDemoBVTutorialActive) return;
			// Save current menu state before closing (to restore when going back)
			previousMenuStateRef.current = {
				firstRow: currentMenuStateRef.current.firstRow,
				secondRow: currentMenuStateRef.current.secondRow,
			};
			setIsDemoIVRModalOpen(false);
			// Close all menus
			setShouldOpenFirstRowMenu(false);
			setShouldOpenSecondRowMenu(false);

			if (benefitVerificationTableRef.current) {
				const tbody = benefitVerificationTableRef.current.querySelector('tbody');
				if (tbody) {
					const rows = tbody.querySelectorAll('tr');
					rows.forEach((row) => {
						const cells = row.querySelectorAll('td');
						if (cells.length > 0) {
							const actionCell = cells[cells.length - 1];
							const trigger = actionCell.querySelector(
								'button, [role="button"]',
							) as HTMLElement;
							if (trigger) {
								// Check if menu is open by looking for open state
								const menuOpen = trigger.getAttribute('aria-expanded') === 'true' ||
									trigger.closest('[data-state="open"]');
								if (menuOpen) {
									trigger.click();
								}
							}
						}
					});
				}
			}
			
			const forceCloseAllMenus = () => {
				const allDivs = document.querySelectorAll('div');
				const openMenus: HTMLElement[] = [];
				
				for (const div of Array.from(allDivs)) {
					const style = window.getComputedStyle(div);
					const zIndex = parseInt(style.zIndex || '0', 10);
					if (
						zIndex >= 1500 &&
						parseFloat(style.borderRadius) > 0 &&
						style.display !== 'none' &&
						style.visibility !== 'hidden' &&
						(div.textContent?.includes('Edit') ||
							div.textContent?.includes('View IVR') ||
							div.textContent?.includes('Order Now') ||
							div.textContent?.includes('Details') ||
							div.textContent?.includes('Support Ticket') ||
							div.textContent?.includes('Void Now'))
					) {
						openMenus.push(div as HTMLElement);
					}
				}
				
				if (openMenus.length > 0) {
					openMenus.forEach((menu) => {
						(menu as HTMLElement).style.display = 'none';
					});
					
					if (benefitVerificationTableRef.current) {
						benefitVerificationTableRef.current.click();
					}
					const clickEvent = new MouseEvent('click', { bubbles: true, cancelable: true });
					document.body.dispatchEvent(clickEvent);
				}
			};
			
			// Close menus immediately
			setTimeout(() => {
				forceCloseAllMenus();
			}, 100);
		});

		tutorialActions.register('openOrderNowModal', () => {
			if (!isDemoBVTutorialActive) return;
			// Save current menu state before closing (to restore when going back)
			previousMenuStateRef.current = {
				firstRow: currentMenuStateRef.current.firstRow,
				secondRow: currentMenuStateRef.current.secondRow,
			};
			
			// FIRST: Close the third row menu immediately when opening Order Now modal
			// This happens when moving from Order Now step to Shipping Method step
			if (currentMenuStateRef.current.secondRow && benefitVerificationTableRef.current) {
				setShouldOpenSecondRowMenu(false);
				const tbody = benefitVerificationTableRef.current.querySelector('tbody');
				if (tbody) {
					const rows = tbody.querySelectorAll('tr');
					if (rows.length > 1) {
						const secondRow = rows[1];
						const cells = secondRow.querySelectorAll('td');
						if (cells.length > 0) {
							const actionCell = cells[cells.length - 1];
							const trigger = actionCell.querySelector(
								'button, [role="button"]',
							) as HTMLElement;
							if (trigger) {
								// Click to close the menu immediately when opening Order Now modal
								trigger.click();
							}
						}
					}
				}
			}
			
			// Close all menus
			setShouldOpenFirstRowMenu(false);
			setShouldOpenSecondRowMenu(false);
			setIsDemoOrderNowModalOpen(true);
		});

		tutorialActions.register('closeOrderNowModal', () => {
			if (!isDemoBVTutorialActive) return;
			// Save current menu state before closing (to restore when going back)
			previousMenuStateRef.current = {
				firstRow: currentMenuStateRef.current.firstRow,
				secondRow: currentMenuStateRef.current.secondRow,
			};
			setIsDemoOrderNowModalOpen(false);
			// Close all menus first
			setShouldOpenFirstRowMenu(false);
			setShouldOpenSecondRowMenu(false);
			// When used in onPrevAction, restore the menu that was open before
			setTimeout(() => {
				const previousState = previousMenuStateRef.current;
				if (previousState.secondRow) {
					setShouldOpenSecondRowMenu(true);
					// Simulate click on the hamburger button of the third row
					if (benefitVerificationTableRef.current) {
						const tbody =
							benefitVerificationTableRef.current.querySelector('tbody');
						if (tbody) {
							const rows = tbody.querySelectorAll('tr');
					if (rows.length > 1) {
							const secondRow = rows[1];
							const cells = secondRow.querySelectorAll('td');
								if (cells.length > 0) {
									const actionCell = cells[cells.length - 1];
									const trigger = actionCell.querySelector(
										'button, [role="button"]',
									) as HTMLElement;
									if (trigger) {
										trigger.click();
									}
								}
							}
						}
					}
				}
			}, 250);
		});

		// Alias for closeOrderNowButton (as requested in JSON)
		tutorialActions.register('closeOrderNowButton', () => {
			if (!isDemoBVTutorialActive) return;
			// Save current menu state before closing (to restore when going back)
			previousMenuStateRef.current = {
				firstRow: currentMenuStateRef.current.firstRow,
				secondRow: currentMenuStateRef.current.secondRow,
			};
			setIsDemoOrderNowModalOpen(false);
			// Close all menus first
			setShouldOpenFirstRowMenu(false);
			setShouldOpenSecondRowMenu(false);
			// When used in onPrevAction, restore the menu that was open before
			setTimeout(() => {
				const previousState = previousMenuStateRef.current;
				if (previousState.secondRow) {
					setShouldOpenSecondRowMenu(true);
					// Simulate click on the hamburger button of the third row
					if (benefitVerificationTableRef.current) {
						const tbody =
							benefitVerificationTableRef.current.querySelector('tbody');
						if (tbody) {
							const rows = tbody.querySelectorAll('tr');
					if (rows.length > 1) {
							const secondRow = rows[1];
							const cells = secondRow.querySelectorAll('td');
								if (cells.length > 0) {
									const actionCell = cells[cells.length - 1];
									const trigger = actionCell.querySelector(
										'button, [role="button"]',
									) as HTMLElement;
									if (trigger) {
										trigger.click();
									}
								}
							}
						}
					}
				}
			}, 250);
		});

		// Alias for closeCreateBVModal (used in onPrevAction)
		tutorialActions.register('closeCreateBVModal', () => {
			if (!isDemoBVTutorialActive) return;
			onCloseDemoBVModal?.();
		});

		// Close the approved action hamburger menu (second row)
		tutorialActions.register('closeApprovedActionHamburgerRef', () => {
			if (!isDemoBVTutorialActive) return;
			// Save current menu state before closing (to restore when going back)
			previousMenuStateRef.current = {
				firstRow: currentMenuStateRef.current.firstRow,
				secondRow: currentMenuStateRef.current.secondRow,
			};
			// Close all menus first
			setShouldOpenFirstRowMenu(false);
			setShouldOpenSecondRowMenu(false);
			// When used in onPrevAction, restore the menu that was open before
			setTimeout(() => {
				const previousState = previousMenuStateRef.current;
				if (previousState.secondRow) {
					setShouldOpenSecondRowMenu(true);
					// Simulate click on the hamburger button of the second row
					if (benefitVerificationTableRef.current) {
						const tbody =
							benefitVerificationTableRef.current.querySelector('tbody');
						if (tbody) {
							const rows = tbody.querySelectorAll('tr');
							if (rows.length > 1) {
								const secondRow = rows[1];
								const cells = secondRow.querySelectorAll('td');
								if (cells.length > 0) {
									const actionCell = cells[cells.length - 1];
									const trigger = actionCell.querySelector(
										'button, [role="button"]',
									) as HTMLElement;
									if (trigger) {
										trigger.click();
									}
								}
							}
						}
					}
				}
			}, 250);
		});

		// Open the action menu for the third row (Approved with Order Now)
		tutorialActions.register('clickApprovedBVActionButton', () => {
			if (!isDemoBVTutorialActive) return;
			// Save current menu state before closing (to restore when going back)
			previousMenuStateRef.current = {
				firstRow: currentMenuStateRef.current.firstRow,
				secondRow: currentMenuStateRef.current.secondRow,
			};
			
			// Close all other menus
			setShouldOpenFirstRowMenu(false);
			setShouldOpenSecondRowMenu(false);
			
			// Force close any open menus in the DOM
			if (benefitVerificationTableRef.current) {
				const tbody = benefitVerificationTableRef.current.querySelector('tbody');
				if (tbody) {
					// Close second row menu if open (most likely to be open)
					if (currentMenuStateRef.current.secondRow) {
						const rows = tbody.querySelectorAll('tr');
						if (rows.length > 1) {
							const secondRow = rows[1];
							const cells = secondRow.querySelectorAll('td');
							if (cells.length > 0) {
								const actionCell = cells[cells.length - 1];
								const trigger = actionCell.querySelector(
									'button, [role="button"]',
								) as HTMLElement;
								if (trigger) {
									trigger.click();
								}
							}
						}
					}
					// Close first row menu if open (just in case)
					if (currentMenuStateRef.current.firstRow) {
						const firstRow = tbody.querySelector('tr:first-child');
						if (firstRow) {
							const cells = firstRow.querySelectorAll('td');
							if (cells.length > 0) {
								const actionCell = cells[cells.length - 1];
								const trigger = actionCell.querySelector(
									'button, [role="button"]',
								) as HTMLElement;
								if (trigger) {
									trigger.click();
								}
							}
						}
					}
					// Close third row menu if open
					if (currentMenuStateRef.current.secondRow) {
						const rows = tbody.querySelectorAll('tr');
						if (rows.length > 1) {
							const secondRow = rows[1];
							const cells = secondRow.querySelectorAll('td');
							if (cells.length > 0) {
								const actionCell = cells[cells.length - 1];
								const trigger = actionCell.querySelector(
									'button, [role="button"]',
								) as HTMLElement;
								if (trigger) {
									trigger.click();
								}
							}
						}
					}
				}
			}
			
			const forceCloseAllMenus = () => {
				const allDivs = document.querySelectorAll('div');
				const openMenus: HTMLElement[] = [];
				
				for (const div of Array.from(allDivs)) {
					const style = window.getComputedStyle(div);
					const zIndex = parseInt(style.zIndex || '0', 10);
					if (
						zIndex >= 1500 &&
						parseFloat(style.borderRadius) > 0 &&
						style.display !== 'none' &&
						style.visibility !== 'hidden' &&
						(div.textContent?.includes('Edit') ||
							div.textContent?.includes('View IVR') ||
							div.textContent?.includes('Order Now') ||
							div.textContent?.includes('Details') ||
							div.textContent?.includes('Support Ticket') ||
							div.textContent?.includes('Void Now'))
					) {
						openMenus.push(div as HTMLElement);
					}
				}
				
				if (openMenus.length > 0) {
					openMenus.forEach((menu) => {
						(menu as HTMLElement).style.display = 'none';
					});
					
					if (benefitVerificationTableRef.current) {
						benefitVerificationTableRef.current.click();
					}
					const clickEvent = new MouseEvent('click', { bubbles: true, cancelable: true });
					document.body.dispatchEvent(clickEvent);
				}
			};
			
			// Close menus immediately
			setTimeout(() => {
				forceCloseAllMenus();
			}, 100);
			
			// Open the third row menu
			setTimeout(() => {
				setShouldOpenSecondRowMenu(true);
				// Simulate click on the hamburger button of the third row
				if (benefitVerificationTableRef.current) {
					const tbody =
						benefitVerificationTableRef.current.querySelector('tbody');
					if (tbody) {
						const rows = tbody.querySelectorAll('tr');
						if (rows.length > 1) {
							const secondRow = rows[1];
							const cells = secondRow.querySelectorAll('td');
							if (cells.length > 0) {
								const actionCell = cells[cells.length - 1];
								const trigger = actionCell.querySelector(
									'button, [role="button"]',
								) as HTMLElement;
								if (trigger) {
									trigger.click();
								}
							}
						}
					}
				}
			}, 500);
		});

		// Action to open the BV actions menu (second row - Approved BV)
		tutorialActions.register('openBVActions', () => {
			if (!isDemoBVTutorialActive) return;
			// Save current menu state before closing (to restore when going back)
			previousMenuStateRef.current = {
				firstRow: currentMenuStateRef.current.firstRow,
				secondRow: currentMenuStateRef.current.secondRow,
			};
			
			// Close other menus first
			setShouldOpenFirstRowMenu(false);
			setShouldOpenSecondRowMenu(false);
			
			// Close menus in DOM if open
			if (benefitVerificationTableRef.current) {
				const tbody = benefitVerificationTableRef.current.querySelector('tbody');
				if (tbody) {
					// Close first row menu if open
					if (currentMenuStateRef.current.firstRow) {
						const firstRow = tbody.querySelector('tr:first-child');
						if (firstRow) {
							const cells = firstRow.querySelectorAll('td');
							if (cells.length > 0) {
								const actionCell = cells[cells.length - 1];
								const trigger = actionCell.querySelector(
									'button, [role="button"]',
								) as HTMLElement;
								if (trigger) {
									trigger.click();
								}
							}
						}
					}
					// Close third row menu if open
					if (currentMenuStateRef.current.secondRow) {
						const rows = tbody.querySelectorAll('tr');
						if (rows.length > 1) {
							const secondRow = rows[1];
							const cells = secondRow.querySelectorAll('td');
							if (cells.length > 0) {
								const actionCell = cells[cells.length - 1];
								const trigger = actionCell.querySelector(
									'button, [role="button"]',
								) as HTMLElement;
								if (trigger) {
									trigger.click();
								}
							}
						}
					}
				}
			}
			
			// Open the second row menu after a delay
			setTimeout(() => {
				setShouldOpenSecondRowMenu(true);
				// Simulate click on the hamburger button of the second row
				if (benefitVerificationTableRef.current) {
					const tbody =
						benefitVerificationTableRef.current.querySelector('tbody');
					if (tbody) {
						const rows = tbody.querySelectorAll('tr');
						if (rows.length > 1) {
							const secondRow = rows[1];
							const cells = secondRow.querySelectorAll('td');
							if (cells.length > 0) {
								const actionCell = cells[cells.length - 1];
								const trigger = actionCell.querySelector(
									'button, [role="button"]',
								) as HTMLElement;
								if (trigger) {
									trigger.click();
								}
							}
						}
					}
				}
			}, 300);
		});

		// Action to close Order Now modal and open the third row action menu
		tutorialActions.register('closeOrderNowModalAndOpenClickApprovedBVActionButton', () => {
			if (!isDemoBVTutorialActive) return;
			// Save current menu state before closing (to restore when going back)
			previousMenuStateRef.current = {
				firstRow: currentMenuStateRef.current.firstRow,
				secondRow: currentMenuStateRef.current.secondRow,
			};
			
			// Close the Order Now modal
			setIsDemoOrderNowModalOpen(false);
			
			// Close other menus first
			setShouldOpenFirstRowMenu(false);
			setShouldOpenSecondRowMenu(false);
			
			// Close menus in DOM if open
			if (benefitVerificationTableRef.current) {
				const tbody = benefitVerificationTableRef.current.querySelector('tbody');
				if (tbody) {
					// Close first row menu if open
					if (currentMenuStateRef.current.firstRow) {
						const firstRow = tbody.querySelector('tr:first-child');
						if (firstRow) {
							const cells = firstRow.querySelectorAll('td');
							if (cells.length > 0) {
								const actionCell = cells[cells.length - 1];
								const trigger = actionCell.querySelector(
									'button, [role="button"]',
								) as HTMLElement;
								if (trigger) {
									trigger.click();
								}
							}
						}
					}
					// Close second row menu if open
					if (currentMenuStateRef.current.secondRow) {
						const rows = tbody.querySelectorAll('tr');
						if (rows.length > 1) {
							const secondRow = rows[1];
							const cells = secondRow.querySelectorAll('td');
							if (cells.length > 0) {
								const actionCell = cells[cells.length - 1];
								const trigger = actionCell.querySelector(
									'button, [role="button"]',
								) as HTMLElement;
								if (trigger) {
									trigger.click();
								}
							}
						}
					}
				}
			}
			
			// Open the third row menu after a delay
			setTimeout(() => {
				setShouldOpenSecondRowMenu(true);
				// Simulate click on the hamburger button of the third row
				if (benefitVerificationTableRef.current) {
					const tbody =
						benefitVerificationTableRef.current.querySelector('tbody');
					if (tbody) {
						const rows = tbody.querySelectorAll('tr');
						if (rows.length > 1) {
							const secondRow = rows[1];
							const cells = secondRow.querySelectorAll('td');
							if (cells.length > 0) {
								const actionCell = cells[cells.length - 1];
								const trigger = actionCell.querySelector(
									'button, [role="button"]',
								) as HTMLElement;
								if (trigger) {
									trigger.click();
								}
							}
						}
					}
				}
			}, 300);
		});

		return () => {
			tutorialActions.unregister('closeBVCreateModal');
			tutorialActions.unregister('clickActionHamburgerRef');
			tutorialActions.unregister('closeBVActions');
			tutorialActions.unregister('openFirstRowMenuAction');
			tutorialActions.unregister('clickApprovedActionHamburgerRef');
			tutorialActions.unregister('openIVRModal');
			tutorialActions.unregister('closeIVRModal');
			tutorialActions.unregister('openOrderNowModal');
			tutorialActions.unregister('closeOrderNowModal');
			tutorialActions.unregister('closeOrderNowButton');
			tutorialActions.unregister('closeCreateBVModal');
			tutorialActions.unregister('closeApprovedActionHamburgerRef');
			tutorialActions.unregister('clickApprovedBVActionButton');
			tutorialActions.unregister('openBVActions');
			tutorialActions.unregister('closeOrderNowModalAndOpenClickApprovedBVActionButton');
		};
	}, [onCloseDemoBVModal, isDemoBVTutorialActive, benefitVerificationTableRef]);

	// Reset menu state when tutorial ends
	useEffect(() => {
		if (!isDemoBVTutorialActive) {
			pendingTimeoutsRef.current.forEach(timeoutId => {
				clearTimeout(timeoutId);
			});
			pendingTimeoutsRef.current = [];
			
			setShouldOpenFirstRowMenu(false);
			setShouldOpenSecondRowMenu(false);
			setIsDemoIVRModalOpen(false);
			setIsDemoOrderNowModalOpen(false);
			
			// Force close any open menus in the DOM
			if (benefitVerificationTableRef.current) {
				const tbody = benefitVerificationTableRef.current.querySelector('tbody');
				if (tbody) {
					const rows = tbody.querySelectorAll('tr');
					rows.forEach((row) => {
						const cells = row.querySelectorAll('td');
						if (cells.length > 0) {
							const actionCell = cells[cells.length - 1];
							const trigger = actionCell.querySelector(
								'button, [role="button"]',
							) as HTMLElement;
							if (trigger) {
								const menuOpen = trigger.getAttribute('aria-expanded') === 'true' ||
									trigger.closest('[data-state="open"]');
								if (menuOpen) {
									trigger.click();
								}
							}
						}
					});
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDemoBVTutorialActive]);

	// Force close all action menus when IVR modal opens
	useEffect(() => {
		if (isDemoIVRModalOpen && isDemoBVTutorialActive) {
			// Close all menu states first
			setShouldOpenFirstRowMenu(false);
			setShouldOpenSecondRowMenu(false);

			const closeMenus = () => {
				// Find all menu content elements with high z-index
				const allDivs = document.querySelectorAll('div');
				const openMenus: HTMLElement[] = [];

				for (const div of Array.from(allDivs)) {
					const style = window.getComputedStyle(div);
					const zIndex = parseInt(style.zIndex || '0', 10);
					if (
						zIndex >= 1500 &&
						parseFloat(style.borderRadius) > 0 &&
						style.display !== 'none' &&
						style.visibility !== 'hidden' &&
						(div.textContent?.includes('Edit') ||
							div.textContent?.includes('View IVR') ||
							div.textContent?.includes('Order Now') ||
							div.textContent?.includes('Details') ||
							div.textContent?.includes('Support Ticket') ||
							div.textContent?.includes('Void Now'))
					) {
						openMenus.push(div as HTMLElement);
					}
				}

				// If we found menus, hide them directly or click outside
				if (openMenus.length > 0) {
					// Method 1: Hide menus directly by setting display: none
					openMenus.forEach((menu) => {
						(menu as HTMLElement).style.display = 'none';
					});

					// Method 2: Click on document body to close menus
					const bodyClick = new MouseEvent('mousedown', {
						bubbles: true,
						cancelable: true,
						view: window,
					});
					document.body.dispatchEvent(bodyClick);

					// Method 3: Also try clicking on the table container
					if (benefitVerificationTableRef.current) {
						const tableClick = new MouseEvent('mousedown', {
							bubbles: true,
							cancelable: true,
							view: window,
						});
						benefitVerificationTableRef.current.dispatchEvent(tableClick);
					}
				}
			};

			// Close menus immediately
			setTimeout(() => {
				closeMenus();
			}, 50);

			// Close menus again after a delay to ensure they close
			setTimeout(() => {
				closeMenus();
			}, 200);
		}
	}, [isDemoIVRModalOpen, isDemoBVTutorialActive, benefitVerificationTableRef]);

	// Register the Edit option inside the opened menu when shouldOpenFirstRowMenu is true
	useEffect(() => {
		if (!shouldOpenFirstRowMenu || !benefitVerificationTableRef.current) {
			registerElement('BVEditOptionRef', null);
			return;
		}

		// Wait for the menu to open and render, then find the "Edit" option
		const timeoutId = setTimeout(() => {
			// Find the menu content by looking for elements with high z-index (menu has zIndex={1500})
			// or elements that are visible and contain menu items
			const allDivs = document.querySelectorAll('div');
			let menuContainer: HTMLElement | null = null;

			// Look for a div with high z-index and border-radius (menu styling)
			for (const div of Array.from(allDivs)) {
				const style = window.getComputedStyle(div);
				const zIndex = parseInt(style.zIndex || '0', 10);
				if (
					zIndex >= 1500 &&
					parseFloat(style.borderRadius) > 0 &&
					style.display !== 'none' &&
					style.visibility !== 'hidden'
				) {
					menuContainer = div;
					break;
				}
			}

			// If we found a menu container, search for "Edit" inside it
			if (menuContainer) {
				const menuItems = menuContainer.querySelectorAll('*');
				for (const item of Array.from(menuItems)) {
					const text = item.textContent?.trim();
					if (text === 'Edit' || text?.startsWith('Edit')) {
						const style = window.getComputedStyle(item as HTMLElement);
						if (
							style.cursor === 'pointer' ||
							item.getAttribute('role') === 'menuitem'
						) {
							registerElement('BVEditOptionRef', item as HTMLElement);
							return;
						}
					}
				}
			}

			// Fallback: search for elements with role="menuitem" that contain "Edit"
			const menuItems = document.querySelectorAll('[role="menuitem"]');
			for (const item of Array.from(menuItems)) {
				if (item.textContent?.trim().includes('Edit')) {
					registerElement('BVEditOptionRef', item as HTMLElement);
					return;
				}
			}

			// Last fallback: search for any clickable element containing "Edit"
			const allClickable = document.querySelectorAll(
				'button, [role="button"], [role="menuitem"], div[style*="cursor: pointer"]',
			);
			for (const el of Array.from(allClickable)) {
				if (el.textContent?.trim() === 'Edit') {
					registerElement('BVEditOptionRef', el as HTMLElement);
					return;
				}
			}
		}, 500); // Wait for the menu to fully render

		return () => {
			clearTimeout(timeoutId);
		};
	}, [shouldOpenFirstRowMenu, registerElement, benefitVerificationTableRef]);

	// Register the hamburger menu trigger for the second row (Approved) after it renders
	useEffect(() => {
		if (!isDemoBVTutorialActive || !benefitVerificationTableRef.current) {
			registerElement('BVApprovedRowAction', null);
			return;
		}

		// Helper function to find and register the trigger
		const findAndRegisterTrigger = () => {
			if (!isTutorialActiveRef.current || !benefitVerificationTableRef.current) {
				return;
			}

			const tableRoot = benefitVerificationTableRef.current;
			const tbody = tableRoot.querySelector('tbody');
			if (!tbody) return;

			const rows = tbody.querySelectorAll('tr');
			if (rows.length < 2) return;

			const secondRow = rows[1];
			if (!secondRow) return;

			const cells = secondRow.querySelectorAll('td');
			if (cells.length === 0) return;

			const actionCell = cells[cells.length - 1];
			if (!actionCell) return;

			let trigger: HTMLElement | null = null;
			const svgIcon = actionCell.querySelector('svg');
			
			if (svgIcon) {
				let parent = svgIcon.parentElement;
				while (parent && parent !== actionCell) {
					const style = window.getComputedStyle(parent);
					if (style.display === 'flex' && style.cursor === 'pointer') {
						trigger = parent as HTMLElement;
						break;
					}
					parent = parent.parentElement;
				}
			}
			
			if (!trigger) {
				trigger = actionCell.querySelector('[role="button"]') as HTMLElement;
			}
			
			if (!trigger && svgIcon) {
				trigger = svgIcon as unknown as HTMLElement;
			}

			if (trigger && isTutorialActiveRef.current) {
				registerElement('BVApprovedRowAction', trigger);
			}
		};

		const timeoutId = setTimeout(() => {
			findAndRegisterTrigger();
		}, 200);

		const intervalId = setInterval(() => {
			if (isTutorialActiveRef.current) {
				findAndRegisterTrigger();
			}
		}, 500);

		return () => {
			clearTimeout(timeoutId);
			clearInterval(intervalId);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDemoBVTutorialActive, registerElement]);

	// Register the View IVR option inside the opened menu of the second row when shouldOpenSecondRowMenu is true
	useEffect(() => {
		if (!shouldOpenSecondRowMenu || !benefitVerificationTableRef.current) {
			registerElement('BVIVRActionRef', null);
			return;
		}

		// Wait for the menu to open and render, then find the "View IVR" option
		const timeoutId = setTimeout(() => {
			// Find the menu content by looking for elements with high z-index (menu has zIndex={1500})
			const allDivs = document.querySelectorAll('div');
			let menuContainer: HTMLElement | null = null;

			// Look for a div with high z-index and border-radius (menu styling)
			for (const div of Array.from(allDivs)) {
				const style = window.getComputedStyle(div);
				const zIndex = parseInt(style.zIndex || '0', 10);
				if (
					zIndex >= 1500 &&
					parseFloat(style.borderRadius) > 0 &&
					style.display !== 'none' &&
					style.visibility !== 'hidden'
				) {
					menuContainer = div;
					break;
				}
			}

			// If we found a menu container, search for "View IVR" inside it
			if (menuContainer) {
				const menuItems = menuContainer.querySelectorAll('*');
				for (const item of Array.from(menuItems)) {
					const text = item.textContent?.trim();
					if (text === 'View IVR' || text?.includes('View IVR')) {
						const style = window.getComputedStyle(item as HTMLElement);
						if (
							style.cursor === 'pointer' ||
							item.getAttribute('role') === 'menuitem'
						) {
							registerElement('BVIVRActionRef', item as HTMLElement);
							return;
						}
					}
				}
			}

			// Fallback: search for elements with role="menuitem" that contain "View IVR"
			const menuItems = document.querySelectorAll('[role="menuitem"]');
			for (const item of Array.from(menuItems)) {
				if (item.textContent?.trim().includes('View IVR')) {
					registerElement('BVIVRActionRef', item as HTMLElement);
					return;
				}
			}

			// Last fallback: search for any clickable element containing "View IVR"
			const allClickable = document.querySelectorAll(
				'button, [role="button"], [role="menuitem"], div[style*="cursor: pointer"]',
			);
			for (const el of Array.from(allClickable)) {
				if (el.textContent?.trim().includes('View IVR')) {
					registerElement('BVIVRActionRef', el as HTMLElement);
					return;
				}
			}
		}, 500); // Wait for the menu to fully render

		return () => {
			clearTimeout(timeoutId);
		};
	}, [shouldOpenSecondRowMenu, registerElement, benefitVerificationTableRef]);

	// Register the Order Now option inside the opened menu of the third row when shouldOpenSecondRowMenu is true
	useEffect(() => {
		if (!shouldOpenSecondRowMenu || !benefitVerificationTableRef.current) {
			registerElement('BVOrderNowOptionRef', null);
			return;
		}

		// Wait for the menu to open and render, then find the "Order Now" option
		const timeoutId = setTimeout(() => {
			// Find the menu content by looking for elements with high z-index (menu has zIndex={1500})
			const allDivs = document.querySelectorAll('div');
			let menuContainer: HTMLElement | null = null;

			// Look for a div with high z-index and border-radius (menu styling)
			for (const div of Array.from(allDivs)) {
				const style = window.getComputedStyle(div);
				const zIndex = parseInt(style.zIndex || '0', 10);
				if (
					zIndex >= 1500 &&
					parseFloat(style.borderRadius) > 0 &&
					style.display !== 'none' &&
					style.visibility !== 'hidden'
				) {
					menuContainer = div;
					break;
				}
			}

			// If we found a menu container, search for "Order Now" inside it
			if (menuContainer) {
				const menuItems = menuContainer.querySelectorAll('*');
				for (const item of Array.from(menuItems)) {
					const text = item.textContent?.trim();
					if (text === 'Order Now' || text?.includes('Order Now')) {
						const style = window.getComputedStyle(item as HTMLElement);
						if (
							style.cursor === 'pointer' ||
							item.getAttribute('role') === 'menuitem'
						) {
							registerElement('BVOrderNowOptionRef', item as HTMLElement);
							return;
						}
					}
				}
			}

			// Fallback: search for elements with role="menuitem" that contain "Order Now"
			const menuItems = document.querySelectorAll('[role="menuitem"]');
			for (const item of Array.from(menuItems)) {
				if (item.textContent?.trim().includes('Order Now')) {
					registerElement('BVOrderNowOptionRef', item as HTMLElement);
					return;
				}
			}

			// Last fallback: search for any clickable element containing "Order Now"
			const allClickable = document.querySelectorAll(
				'button, [role="button"], [role="menuitem"], div[style*="cursor: pointer"]',
			);
			for (const el of Array.from(allClickable)) {
				if (el.textContent?.trim().includes('Order Now')) {
					registerElement('BVOrderNowOptionRef', el as HTMLElement);
					return;
				}
			}
		}, 500); // Wait for the menu to fully render

		return () => {
			clearTimeout(timeoutId);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldOpenSecondRowMenu, registerElement]);

	// Register the hamburger menu trigger for the third row (Approved with Order Now) after it renders
	useEffect(() => {
		if (!isDemoBVTutorialActive || !benefitVerificationTableRef.current) {
			registerElement('BVOrderNowActionRef', null);
			return;
		}

		const findAndRegisterTrigger = () => {
			if (!isTutorialActiveRef.current || !benefitVerificationTableRef.current) {
				return;
			}

			const tableRoot = benefitVerificationTableRef.current;
			const tbody = tableRoot.querySelector('tbody');
			if (!tbody) return;

			const rows = tbody.querySelectorAll('tr');
			if (rows.length < 2) return;

			const secondRow = rows[1];
			if (!secondRow) return;

			const cells = secondRow.querySelectorAll('td');
			if (cells.length === 0) return;

			const actionCell = cells[cells.length - 1];
			if (!actionCell) return;

			let trigger: HTMLElement | null = null;
			const svgIcon = actionCell.querySelector('svg');
			
			if (svgIcon) {
				let parent = svgIcon.parentElement;
				while (parent && parent !== actionCell) {
					const style = window.getComputedStyle(parent);
					if (style.display === 'flex' && style.cursor === 'pointer') {
						trigger = parent as HTMLElement;
						break;
					}
					parent = parent.parentElement;
				}
			}
			
			if (!trigger) {
				trigger = actionCell.querySelector('[role="button"]') as HTMLElement;
			}
			
			if (!trigger && svgIcon) {
				trigger = svgIcon as unknown as HTMLElement;
			}

			if (trigger && isTutorialActiveRef.current) {
				registerElement('BVOrderNowActionRef', trigger);
			}
		};

		const timeoutId = setTimeout(() => {
			findAndRegisterTrigger();
		}, 200);

		const intervalId = setInterval(() => {
			if (isTutorialActiveRef.current) {
				findAndRegisterTrigger();
			}
		}, 500);

		return () => {
			clearTimeout(timeoutId);
			clearInterval(intervalId);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDemoBVTutorialActive, registerElement]);

	// Register table icon refs (Search, Filter Columns, Density, Fullscreen, Filter Dropdown)
	useEffect(() => {
		if (!isDemoBVTutorialActive || !benefitVerificationTableRef.current) {
			registerElement('BVSearchIconRef', null);
			registerElement('BVFilterColumnsIconRef', null);
			registerElement('BVDensityIconRef', null);
			registerElement('BVFullscreenIconRef', null);
			registerElement('BVFilterDropdownRef', null);
			return;
		}

		const timeoutId = setTimeout(() => {
			// Find Search icon (AiOutlineSearch)
			const searchIcon = benefitVerificationTableRef.current?.querySelector(
				'[aria-label*="Search"], [title*="Search"], svg[class*="search"]',
			) as HTMLElement;
			if (searchIcon) {
				// Find the button parent
				const searchButton = searchIcon.closest('button') || searchIcon;
				registerElement('BVSearchIconRef', searchButton);
			}

			// Find Filter Columns icon (VerticalBarsIcon)
			const filterColumnsIcon = benefitVerificationTableRef.current?.querySelector(
				'[aria-label*="Show/hide columns"], [aria-label*="columns"], [title*="column"]',
			) as HTMLElement;
			if (filterColumnsIcon) {
				const filterButton = filterColumnsIcon.closest('button') || filterColumnsIcon;
				registerElement('BVFilterColumnsIconRef', filterButton);
			}

			// Find Density icon (HorizontalBarsIcon, RxHamburgerMenu, or HiOutlineMenuAlt4)
			const densityIcon = benefitVerificationTableRef.current?.querySelector(
				'[aria-label*="Table density"], [title*="Table density"], [title*="density"]',
			) as HTMLElement;
			if (densityIcon) {
				const densityButton = densityIcon.closest('button') || densityIcon;
				registerElement('BVDensityIconRef', densityButton);
			}

			// Find Fullscreen icon (FaExpand or RiFullscreenExitLine)
			const fullscreenIcon = benefitVerificationTableRef.current?.querySelector(
				'[aria-label*="Full screen"], [title*="Full screen"], [title*="Exit"]',
			) as HTMLElement;
			if (fullscreenIcon) {
				const fullscreenButton = fullscreenIcon.closest('button') || fullscreenIcon;
				registerElement('BVFullscreenIconRef', fullscreenButton);
			}

			let statusFilterInput: HTMLElement | null = null;
			
			const allHeaders = benefitVerificationTableRef.current?.querySelectorAll('thead tr:last-child th');
			if (allHeaders) {
				Array.from(allHeaders).forEach((header, index) => {

					const headerText = header.textContent?.trim() || '';
					if (headerText === 'Status' || headerText.includes('Status')) {

						const thead = header.closest('thead');
						if (thead) {
							const filterRow = thead.querySelector('tr:first-child');
							if (filterRow) {
								const filterCells = filterRow.querySelectorAll('th');
								if (filterCells[index]) {
									// The input has placeholder "Filter", not "Search"
									const input = filterCells[index].querySelector('input[placeholder="Filter"]') as HTMLElement;
									if (input) {
										statusFilterInput = input;
									}
								}
							}
						}
					}
				});
			}
			
			if (!statusFilterInput) {
				const filterRow = benefitVerificationTableRef.current?.querySelector('thead tr:first-child');
				if (filterRow) {
					const filterInputs = filterRow.querySelectorAll('input[placeholder="Filter"]');
					if (filterInputs.length >= 7) {
						// Status column is usually the 7th input (index 6)
						statusFilterInput = filterInputs[6] as HTMLElement;
					}
				}
			}
			
			if (statusFilterInput) {
				registerElement('BVFilterDropdownRef', statusFilterInput);
			}
		}, 300);

		return () => {
			clearTimeout(timeoutId);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDemoBVTutorialActive, registerElement]);

	// Register the hamburger menu trigger for the first demo row after it renders.
	useEffect(() => {
		if (!isDemoBVTutorialActive || !benefitVerificationTableRef.current) {
			registerElement('BVActionHamburgerRef', null);
			return;
		}

		const findAndRegisterTrigger = () => {
			if (!isTutorialActiveRef.current || !benefitVerificationTableRef.current) {
				return;
			}

			const tableRoot = benefitVerificationTableRef.current;
			const tbody = tableRoot.querySelector('tbody');
			if (!tbody) return;

			const firstRow = tbody.querySelector('tr:first-child');
			if (!firstRow) return;

			const cells = firstRow.querySelectorAll('td');
			if (cells.length === 0) return;

			const actionCell = cells[cells.length - 1];
			if (!actionCell) return;

			let trigger: HTMLElement | null = null;
			const svgIcon = actionCell.querySelector('svg');
			
			if (svgIcon) {
				let parent = svgIcon.parentElement;
				while (parent && parent !== actionCell) {
					const style = window.getComputedStyle(parent);
					if (style.display === 'flex' && style.cursor === 'pointer') {
						trigger = parent as HTMLElement;
						break;
					}
					parent = parent.parentElement;
				}
			}
			
			if (!trigger) {
				trigger = actionCell.querySelector('[role="button"]') as HTMLElement;
			}
			
			if (!trigger && svgIcon) {
				trigger = svgIcon as unknown as HTMLElement;
			}

			if (trigger && isTutorialActiveRef.current) {
				registerElement('BVActionHamburgerRef', trigger);
			}
		};

		const timeoutId = setTimeout(() => {
			findAndRegisterTrigger();
		}, 200);

		const intervalId = setInterval(() => {
			if (isTutorialActiveRef.current) {
				findAndRegisterTrigger();
			}
		}, 500);

		return () => {
			clearTimeout(timeoutId);
			clearInterval(intervalId);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDemoBVTutorialActive, registerElement]);


	// Helper function to close any open menu
	const closeOpenMenu = () => {
		if (!benefitVerificationTableRef.current) return;
		
		const tbody = benefitVerificationTableRef.current.querySelector('tbody');
		if (!tbody) return;
		
		const rows = tbody.querySelectorAll('tr');
		rows.forEach((row) => {
			const cells = row.querySelectorAll('td');
			if (cells.length > 0) {
				const actionCell = cells[cells.length - 1];
				const trigger = actionCell.querySelector(
					'button, [role="button"]',
				) as HTMLElement;
				if (trigger) {
					trigger.click();
				}
			}
		});
	};

	// Mocked action for View IVR during tutorial
	const handleTutorialViewIVR = () => {
		if (!isDemoBVTutorialActive) return;
		
		// Close the second row menu first to prevent third row menu from opening
		if (shouldOpenSecondRowMenu && benefitVerificationTableRef.current) {
			const tbody = benefitVerificationTableRef.current.querySelector('tbody');
			if (tbody) {
				const rows = tbody.querySelectorAll('tr');
				if (rows.length > 1) {
					const secondRow = rows[1];
					const cells = secondRow.querySelectorAll('td');
					if (cells.length > 0) {
						const actionCell = cells[cells.length - 1];
						const trigger = actionCell.querySelector(
							'button, [role="button"]',
						) as HTMLElement;
						if (trigger) {
							trigger.click();
						}
					}
				}
			}
		}
		
		setTimeout(() => {
			setIsDemoIVRModalOpen(true);
		}, 100);
	};


	const handleTutorialNoOp = () => {
		if (!isDemoBVTutorialActive) return;
		closeOpenMenu();
	};

	// Mocked action for Order Now during tutorial (second row = Approved)
	const handleTutorialOrderNow = (item: ColumnData) => {
		if (!isDemoBVTutorialActive) return;
		
		const isSecondDemoRow =
			(item as ColumnData & { bv_product_status?: string }).bv_product_status === 'Approved';

		if (isSecondDemoRow) {
			// Close second row menu first, then open demo modal
			if (shouldOpenSecondRowMenu && benefitVerificationTableRef.current) {
				const tbody = benefitVerificationTableRef.current.querySelector('tbody');
				if (tbody) {
					const rows = tbody.querySelectorAll('tr');
					if (rows.length > 1) {
						const secondRow = rows[1];
						const cells = secondRow.querySelectorAll('td');
						if (cells.length > 0) {
							const actionCell = cells[cells.length - 1];
							const trigger = actionCell.querySelector(
								'button, [role="button"]',
							) as HTMLElement;
							if (trigger) {
								trigger.click();
							}
						}
					}
				}
			}
			
			setTimeout(() => {
				setIsDemoOrderNowModalOpen(true);
			}, 100);
		} else {
			closeOpenMenu();
		}
	};

	return {
		shouldOpenFirstRowMenu,
		shouldOpenSecondRowMenu,
		isDemoIVRModalOpen,
		isDemoOrderNowModalOpen,
		setIsDemoIVRModalOpen,
		setIsDemoOrderNowModalOpen,
		mockedActions: {
			handleViewIVR: handleTutorialViewIVR,
			handleVoidNow: handleTutorialNoOp,
			handleOrderNow: handleTutorialOrderNow,
			handleReorder: handleTutorialNoOp,
			handleEdit: handleTutorialNoOp,
			handleDetails: handleTutorialNoOp,
			handleViewPRDocument: handleTutorialNoOp,
			handleViewDocument: handleTutorialNoOp,
			handleSupportTicket: handleTutorialNoOp,
		},
	};
};

